/**
 * Factory de colas con retry, backoff y persistencia
 *
 * @module services/baseQueue
 */

/**
 * Crea una cola con procesamiento, persistencia y retry
 *
 * @param {Object} config
 * @param {Function} config.processItem - async (item) => Promise — procesa un item
 * @param {Function} config.persist - async (queue) => void — persiste la cola
 * @param {Function} config.restore - async () => Array — restaura la cola
 * @param {number} [config.maxRetries=3] - Máximo de reintentos
 * @param {number} [config.backoffBaseMs=1000] - Base para backoff exponencial
 * @returns {Object} Cola con API: add, remove, getState, retryAll, subscribe
 */
export function createBaseQueue(config) {
  const { processItem, persist, restore, maxRetries = 3, backoffBaseMs = 1000 } = config;

  let queue = [];
  let idCounter = 0;
  let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  let isProcessing = false;
  let isReplaying = false;
  const subscribers = new Set();

  function notify() {
    const state = getState();
    for (const fn of subscribers) {
      try { fn(state); } catch { /* ignore */ }
    }
  }

  function getState() {
    return {
      queue: [...queue],
      isOnline,
      isProcessing,
      pendingCount: queue.filter(i => i.status !== 'completed' && i.status !== 'auth_error' && i.status !== 'orphan').length
    };
  }

  function subscribe(fn) {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }

  async function add(itemData) {
    const item = {
      ...itemData,
      id: ++idCounter,
      status: 'pending',
      retries: 0,
      addedAt: Date.now()
    };
    queue.push(item);
    notify();

    if (isOnline && !isReplaying) {
      await processOne(item);
    }

    try { await persist(queue); } catch { /* ignore */ }
    return item.id;
  }

  async function processOne(item) {
    try {
      isProcessing = true;
      notify();
      item.status = 'processing';
      await processItem(item);
      queue = queue.filter(i => i.id !== item.id);
    } catch (err) {
      item.retries = (item.retries || 0) + 1;
      if (item.retries >= maxRetries) {
        item.status = 'failed';
      } else {
        item.status = 'pending';
        const delay = backoffBaseMs * Math.pow(2, item.retries);
        setTimeout(() => {
          if (isOnline && !isReplaying) processOne(item);
        }, delay);
      }
    } finally {
      isProcessing = queue.some(i => i.status === 'processing');
      notify();
    }
  }

  function remove(id) {
    queue = queue.filter(i => i.id !== id);
    notify();
    persist(queue).catch(() => {});
  }

  async function retryAll() {
    try {
      isReplaying = true;
      const pending = queue.filter(i => i.status === 'pending' || i.status === 'failed');
      for (const item of pending) {
        if (!isOnline) break;
        await processOne(item);
      }
    } finally {
      isReplaying = false;
      try { await persist(queue); } catch { /* ignore */ }
      notify();
    }
  }

  // Online/offline listeners
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      isOnline = true;
      notify();
      retryAll();
    });

    window.addEventListener('offline', () => {
      isOnline = false;
      notify();
    });
  }

  // Restore queue on init
  (async () => {
    try {
      const restored = await restore();
      if (restored && restored.length > 0) {
        for (const item of restored) {
          item.id = ++idCounter;
          if (item.status === 'processing') item.status = 'pending';
        }
        queue = restored;
        notify();
        if (isOnline) retryAll();
      }
    } catch { /* ignore */ }
  })();

  return {
    add,
    remove,
    getState,
    retryAll,
    subscribe,
    get isReplaying() { return isReplaying; }
  };
}
